import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import s from './PeriodicSummarySettings.module.scss';
import { FormattedMessage, injectIntl } from 'react-intl';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Banner from '../Banner/Banner';
import { initialModelState } from '../../reducers/initialState';
import PeriodicSummarySettingListTable from './PeriodicSummarySettingsListTable';
import { WidgetVisibility, IsWidgetAccessible } from '../../utils/widgetManager';
import SearchBar from '../SearchBar/SearchBar';
import Button from 'react-bootstrap/Button';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Cookies from 'js-cookie';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import { getModelCategoryPermission } from '../../utils';
import { useParams } from 'react-router-dom';
import { DEFAULT_PAGE_SIZE, ModelCategoryPermission } from '../../constants';
import Breadcrumb from '../WSAControls/Breadcrumb/BreadCrumb';
import ModelConfigurationContentTemplate from '../ModelTab/ModelConfigurationContentTemplate';
import Pagination from '../Pagination/Pagination';


const PeriodicSummarySettingsList = ({
  periodicSummarySetting = { ...initialModelState.modelManager.selectedModel.PeriodicSummarySettings },
  intl,
  ReadOnly,
  userProfileWidgets,
  getPeriodicSummarySettings,
  savePeriodicSummarySetting,
  describePeriodicSummarySetting,
  changePeriodicSummarySetting,
  setPeriodicSummarySettingShowScreenStatus,
  userModelCategories,
  setCurrentPage,
  uploadPeriodicSummarySettings,
  currentTimezone
}) => {

  let { ModelCategoryId, ModelId } = useParams();

  let filter = '';
  let offset = 0;
  let limit = 1000;


  useEffect(() => {
    getPeriodicSummarySettings(offset, limit, filter, ModelCategoryId, ModelId);
  }, [getPeriodicSummarySettings, offset, limit, filter, ModelCategoryId, ModelId]);

  const [currentEditId, setCurrentEditId] = useState(-1);
  const [active, setActive] = useState(false);
  const [delPeriodicSummarySettingId, setPeriodicSummarySettingId] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [uploadPeriodicSettingActive, setUploadPeriodicSettingActive] = useState(false);
  const envName = process.env.REACT_APP_ENV_NAME_SHORT;
  const addNewRef = useRef(null);
  const isAddAllowed = IsWidgetAccessible(userProfileWidgets, 'PERIODICSUMMARYSETTINGSADD');


  const onPageChangeHandler = currentPage => {
    setCurrentPage(currentPage);
  };


  const searchHandler = value => {
    setCurrentPage(1);
    setSearchText(value);
  };


  const disableModeHandler = status => {
    setPeriodicSummarySettingShowScreenStatus(status);
  };


  const setAddHandler = () => {
    setPeriodicSummarySettingShowScreenStatus(true);
    setCurrentEditId(0);
    window.scrollTo(0, addNewRef.current.offsetTop);
  };


  const setEditHandler = periodicSummarySettingId => {
    setCurrentEditId(periodicSummarySettingId);
    if (periodicSummarySettingId === -1) {
      changePeriodicSummarySetting({
        SelectedPeriodicSummarySetting: {
          ...initialModelState.modelManager.selectedModel.PeriodicSummarySettings.SelectedPeriodicSummarySetting,
          ModelId: ModelId,
          ModelCategoryId: ModelCategoryId
        }
      });
      setPeriodicSummarySettingShowScreenStatus(false);
    } else {
      setPeriodicSummarySettingShowScreenStatus(true);
    }
  };

  const filterData = () => {
    let filteredList = periodicSummarySetting.PeriodicSummarySettingsList;
    filteredList = periodicSummarySetting.PeriodicSummarySettingsList.filter(function (item) {
      return item.Name.toLowerCase().includes(searchText.toLowerCase());
    });
    return filteredList;
  };






  const removePeriodicSummarySettingClick = periodicSummarySettingId => {
    setActive(true);
    setPeriodicSummarySettingId(periodicSummarySettingId);
  };

  const DeletePeriodicSummarySetting = () => {
    let saveData = periodicSummarySetting.SelectedPeriodicSummarySetting;
    saveData.MarkAsDeleted = true;
    saveData.ModelId = ModelId;
    saveData.ModelCategoryId = ModelCategoryId;
    saveData.UpdatedBy = Cookies.get(`userid-${envName}`) || 0;
    saveData.PeriodicSummarySettingId = delPeriodicSummarySettingId;
    savePeriodicSummarySetting(saveData, 'PERIODICSUMMARYSETTINGSDELETE');
  };

  const handleClose = () => {
    setActive(false);
    setPeriodicSummarySettingId(0);
  };

  const handleConfirm = () => {
    DeletePeriodicSummarySetting();
    handleClose();
  };


  const uploadPeriodicSummarySettingClick = () => {
    setUploadPeriodicSettingActive(true);
  };

  const handlePeriodicSettingsClose = () => {
    setUploadPeriodicSettingActive(false);
  };

  const handlePeriodicSettingsConfirm = () => {
    uploadPeriodicSummarySettings(ModelCategoryId, ModelId);
    handlePeriodicSettingsClose();
  };


  let messageId = (periodicSummarySetting && periodicSummarySetting.displayMessageCode) || 'none';
  const messageText = intl.formatMessage({ id: messageId, defaultMessage: messageId });

  const isUpdateAllowed = IsWidgetAccessible(userProfileWidgets, 'PERIODICSUMMARYSETTINGSEDIT');
  const isDeleteAllowed = IsWidgetAccessible(userProfileWidgets, 'PERIODICSUMMARYSETTINGSDELETE');

  const dialogTitle = intl.formatMessage({
    id: 'periodicSummarySettings.periodicSummarySettingsConfirmDelete',
    defaultMessage: 'Are you sure you want to delete this periodic summary setting?'
  });

  const uploadDialogTitle = intl.formatMessage({
    id: 'periodicSummarySettings.uploadPeriodicSummarySettingsConfirm',
    defaultMessage: 'Are you sure you want to publish periodic summary settings to all the units?'
  });



  const breadcrumbItems = [
    { label: <FormattedMessage id="modelcategory.modelcategory" defaultMessage="Model Category" />, href: '/operational-setup/model-category-list' },
    { label: <FormattedMessage id="model.models" defaultMessage="Models" />, href: `/operational-setup/model-category-configuration/${ModelCategoryId}/model-list` },
    { label: <FormattedMessage id="periodicSummarySettings.managePeriodicSummarySettings" defaultMessage="Manage Periodic Summary Settings" /> } // Last item has no href (current page)
  ];

  let isModelCategoryOwner = getModelCategoryPermission(userModelCategories, ModelCategoryId, ModelCategoryPermission.Owner)
  const dataObj = Object.values(filterData());

  return (
    <div className={s.periodicSummarySettings}>
      {periodicSummarySetting.isLoading && <LoadingSpinner />}
      {periodicSummarySetting.showBanner && (
        <Banner
          failureText={messageText}
          showBanner={periodicSummarySetting.showBanner}
          status={periodicSummarySetting.isOpSuccessfull}
          successText={messageText}
        />
      )}
      <ConfirmDialog id="deleteDialog" title={dialogTitle} onConfirm={handleConfirm} onClose={handleClose} showDialog={active} />
      <ConfirmDialog id="uploadDialog" title={uploadDialogTitle} onConfirm={handlePeriodicSettingsConfirm} onClose={handlePeriodicSettingsClose} showDialog={uploadPeriodicSettingActive} />

      <ModelConfigurationContentTemplate selectedPage="periodicSummarySettings" userProfileWidgets={userProfileWidgets} intl={intl}>
        <div className={s.contentWrapper}>
          <div className={s.componentHeader}>
            <div className={s.subHeading}>
              <FormattedMessage id="periodicSummarySettings.managePeriodicSummarySettings" defaultMessage="Manage Periodic Summary Settings" />
              <span className={s.circle}>
                {periodicSummarySetting.PeriodicSummarySettingsList.length}
              </span>
            </div>
          </div>
          <div className='pb-3'>
            <Breadcrumb items={breadcrumbItems} />
          </div>
          <Row className={s.topRow}>
            <Col sm={6} lg={4}>
              <SearchBar
                searchHandler={searchHandler}
                clearSearchInVisible={false}
                disabled={periodicSummarySetting.showAddEditScreen}
                placeHolderTextId="common.search"
                data-unittest="searchPeriodicSummarySettings"
              />
            </Col>
            <Col sm={6} lg={8}>
              {!ReadOnly && isModelCategoryOwner ? (
                <>
                  <Button
                    className={s.actionbutton}
                    style={WidgetVisibility(userProfileWidgets, 'PERIODICSUMMARYSETTINGSADD')}
                    onClick={setAddHandler}
                    disabled={periodicSummarySetting.showAddEditScreen}
                    data-unittest="addPeriodicSummarySettingButton"
                  >
                    + <FormattedMessage id="common.addNew" defaultMessage="ADD NEW" />
                  </Button>
                  <Button
                    className={isAddAllowed ? s.uploadbutton : s.actionbutton}
                    style={WidgetVisibility(userProfileWidgets, 'PERIODIC_SUMMARY_SETTING_UPLOAD_FILE')}
                    onClick={uploadPeriodicSummarySettingClick}
                    data-unittest="uploadFileComponentPosition"
                  >
                    <FormattedMessage id="periodicSummarySettings.publishPeriodicSummarySettings" defaultMessage="PUBLISH SETTINGS" />
                  </Button>
                </>

              ) : (
                ''
              )}
            </Col>
          </Row>


          <div>
            <Pagination
              data={dataObj}
              pageSize={DEFAULT_PAGE_SIZE}
              onPageChange={onPageChangeHandler}
              startingPage={periodicSummarySetting.currentPage}>
              <PeriodicSummarySettingListTable
                setEditHandler={setEditHandler}
                selectedPeriodicSummarySettingId={currentEditId}
                savePeriodicSummarySetting={savePeriodicSummarySetting}
                periodicSummarySetting={periodicSummarySetting}
                describePeriodicSummarySetting={describePeriodicSummarySetting}
                changePeriodicSummarySetting={changePeriodicSummarySetting}
                UpdateAllowed={isUpdateAllowed}
                DeleteAllowed={isDeleteAllowed}
                disableMode={periodicSummarySetting.showAddEditScreen}
                disableModeHandler={disableModeHandler}
                addNewRef={addNewRef}
                ReadOnly={ReadOnly}
                removePeriodicSummarySetting={removePeriodicSummarySettingClick}
                currentTimezone={currentTimezone}
                isModelCategoryOwner={isModelCategoryOwner}
              />
            </Pagination>
          </div>
        </div>
      </ModelConfigurationContentTemplate>
    </div>
  );
};



PeriodicSummarySettingsList.propTypes = {
  userProfileWidgets: PropTypes.object.isRequired,
  getPeriodicSummarySettings: PropTypes.func.isRequired,
  savePeriodicSummarySetting: PropTypes.func.isRequired,
  describePeriodicSummarySetting: PropTypes.func.isRequired,
  changePeriodicSummarySetting: PropTypes.func.isRequired,
  setPeriodicSummarySettingShowScreenStatus: PropTypes.func.isRequired
};

export default injectIntl(PeriodicSummarySettingsList);
